import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  BASE_URL, DEFAULT_IMAGE, NOINDEX_PREFIXES, canonicalUrl, getRouteSeo,
} from '@/lib/seoConfig';

const normalizePath = (path) => path === '/' ? '/' : path.replace(/\/+$/, '');
const absoluteImage = (image) => new URL(image || DEFAULT_IMAGE, BASE_URL).toString();

const upsertMeta = (selector, attributes, content) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
  element.setAttribute('data-seo-managed', 'true');
};

export const Seo = ({ title, description, path, image, type = 'website', noIndex = false, schemas = [] }) => {
  useEffect(() => {
    const url = canonicalUrl(normalizePath(path));
    const shareImage = absoluteImage(image);
    document.title = title;

    upsertMeta('meta[name="description"]', { name: 'description' }, description);
    upsertMeta('meta[name="robots"]', { name: 'robots' }, noIndex ? 'noindex, nofollow' : 'index, follow');
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }, type);
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, 'SŌHM');
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale' }, 'en_IN');
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, title);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, description);
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, url);
    upsertMeta('meta[property="og:image"]', { property: 'og:image' }, shareImage);
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, shareImage);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);
    canonical.setAttribute('data-seo-managed', 'true');

    document.head.querySelectorAll('script[data-seo-json-ld]').forEach((node) => node.remove());
    if (!noIndex) {
      schemas.filter(Boolean).forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.dataset.seoJsonLd = 'true';
        script.text = JSON.stringify(schema).replace(/</g, '\\u003c');
        document.head.appendChild(script);
      });
    }
  }, [title, description, path, image, type, noIndex, schemas]);

  return null;
};

export const RouteSeo = () => {
  const location = useLocation();
  const path = normalizePath(location.pathname);
  const routeSeo = getRouteSeo(path);
  const isAuthCallback = location.hash.includes('session_id=');
  const isPrivate = NOINDEX_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
  const isProduct = path.startsWith('/collectible/');

  if (routeSeo && !isAuthCallback && !isPrivate) return <Seo {...routeSeo} />;

  return (
    <Seo
      title="SŌHM"
      description="SŌHM account and transaction page."
      path={path}
      noIndex={isAuthCallback || isPrivate || isProduct || !routeSeo}
    />
  );
};